import {
  VoteDocument,
  JinroGamePlayinfoDocument,
  JinroPlayerDocument,
} from "@/services/datatypes";
import { JinroPlayStatus } from "@/services/jinro-play-status";
import { MAX_EXTRA_VOTE_COUNT } from "@/services/constants";
import { JinroRoles } from "@/services/jinro-roles";

const timeSince = (date: Date, postFix: string): string => {
  const now = Math.floor(new Date().getTime());
  const seconds = Math.floor((now - date.getTime()) / 1000);
  let interval = Math.floor(seconds / 31536000);

  if (interval > 1) {
    return interval + "年前";
  }
  interval = Math.floor(seconds / 2592000);
  if (interval > 1) {
    // TODO 週間前にくくっていきたい
    return interval + "ヶ月前";
  }
  interval = Math.floor(seconds / 86400);
  if (interval > 1) {
    // TODO 週間前にくくっていきたい
    return interval + "日前";
  }
  interval = Math.floor(seconds / 3600);
  if (interval > 1) {
    return interval + `時間前${postFix}`;
  }
  interval = Math.floor(seconds / 60);
  if (interval > 1) {
    if (interval <= 10) {
      return interval + `分前${postFix}`;
    }
    return Math.round(interval / 10) * 10 + `分前${postFix}`;
  }
  return `1分前${postFix}`;
};

export const onlineTimeSince = (date: Date): string => {
  return timeSince(date, "にオンライン");
};

export const chatSentTimeSince = (date: Date): string => {
  return timeSince(date, "");
};

export const getPlayerIdVotedCountAssociativeList = (
  votes: Array<VoteDocument>
): { [playerId: string]: number } => {
  // VotesのtoPlayers抜き出す
  const toPlayers = votes.map((vote) => {
    return {
      id: vote.toPlayer.id,
      name: vote.toPlayer.playerName,
    };
  });

  // VotesのtoPlayersの出現数をだす
  const list: { [playerId: string]: number } = {};
  toPlayers.forEach((player) => {
    if (list[player.id]) {
      list[player.id] = list[player.id] + 1;
    } else {
      list[player.id] = 1;
    }
  });

  return list;
};

const getIdToNameAssociativeList = (
  votes: Array<VoteDocument>
): { [id: string]: string } => {
  // VotesのtoPlayers抜き出す
  const toPlayers = votes.map((vote) => {
    return {
      id: vote.toPlayer.id,
      name: vote.toPlayer.playerName,
    };
  });

  // idとnameの連想配列つくる
  const idToName: { [id: string]: string } = {};
  toPlayers.forEach((player) => {
    idToName[player.id] = player.name;
  });

  return idToName;
};

export const getVoteRankingTopList = (
  votes: Array<VoteDocument>,
  topN: number | null
): Array<{
  index: number;
  count: number;
  playerNames: Array<string>;
}> => {
  const list = getPlayerIdVotedCountAssociativeList(votes);

  // 出現回数の配列をとる. ユニークにして、降順に並べ替え
  let countArry: Array<number> = [];
  for (const key in list) {
    countArry.push(list[key]);
  }
  countArry = [...new Set(countArry)];
  countArry.sort((a, b) => {
    return b - a;
  });
  let countArryTopN = [];
  if (topN === null || countArry.length <= topN) {
    countArryTopN = countArry;
  } else {
    countArryTopN = countArry.slice(0, topN);
  }

  const idToNameList = getIdToNameAssociativeList(votes);
  // 出現回数と、それに対応するtoPlayersの連想配列つくる
  const countToPlayers: {
    [count: number]: Array<string>;
  } = {};
  Object.keys(list).forEach((key) => {
    const count = list[key];
    if (countToPlayers[count]) {
      countToPlayers[count] = countToPlayers[count].concat([idToNameList[key]]);
    } else {
      countToPlayers[count] = [idToNameList[key]];
    }
  });

  const rankingList: Array<{
    index: number;
    count: number;
    playerNames: Array<string>;
  }> = [];
  let index = 1;
  countArryTopN.forEach((count) => {
    const arry = {
      index: index,
      count: count,
      playerNames: countToPlayers[count],
    };
    rankingList.push(arry);
    index += 1;
  });

  return rankingList;
};

export const getVoteResult = (
  votes: Array<VoteDocument>,
  jinroPlayinfo: JinroGamePlayinfoDocument | undefined
): string => {
  if (!jinroPlayinfo) {
    return "";
  }

  const mostVotesObject = getVoteRankingTopList(votes, 1)[0];
  if (mostVotesObject.playerNames.length === 1) {
    return `<strong>${mostVotesObject.playerNames}</strong>が処刑されます`;
  }
  if (
    jinroPlayinfo.playStatus === JinroPlayStatus.ExtraVote &&
    jinroPlayinfo.extraVoteCount === MAX_EXTRA_VOTE_COUNT
  ) {
    return `${MAX_EXTRA_VOTE_COUNT}回の再投票でも決まらなかっため、<strong>処刑者なしとなります</strong>`;
  }
  return "<strong>上記プレイヤーでの再投票となります</strong>";
};

export const attachRoleToPlayers = (
  players: Array<JinroPlayerDocument>,
  playersWithRole: Array<JinroPlayerDocument>
): Array<JinroPlayerDocument> => {
  const arry = players.map((player) => {
    const playerWithRole = playersWithRole.find((pwr) => {
      return pwr.id === player.id;
    });

    return playerWithRole
      ? {
          ...player,
          role: playerWithRole.role,
        }
      : {
          ...player,
        };
  });

  return arry;
};

export const attachJinroRoleToPlayers = (
  players: Array<JinroPlayerDocument>,
  playersWithJinroRole: Array<JinroPlayerDocument>
): Array<JinroPlayerDocument> => {
  const jinroIds = playersWithJinroRole.map((player) => {
    return player.id;
  });

  const arry = players.map((player) => {
    return jinroIds.includes(player.id)
      ? {
          ...player,
          role: JinroRoles.Jinro,
        }
      : {
          ...player,
        };
  });

  return arry;
};
